/**
 * @file ほかの端末と同じページを開く連動の、端末の中の状態である。
 *
 * @what: 次の4つを持つ。
 *   - 連動を入れているか (端末ごとに localStorage に覚える。最初は切れている)
 *   - この画面を開いている間の端末の印 (読み込むたびに作り直す乱数)
 *   - すでに見た閲覧位置の時刻 (自分が送った・ついていった時刻)
 *   - 送っている途中の閲覧位置の数と、ついていったばかりのページ (送り返さないための印)
 * @why: 端末の印をタブごとにするのは、同じ端末で2つのタブを開いても互いについていけるようにするためである。
 *   送っている途中の数を持つのは、自分の送信が届く前にほかの端末の閲覧位置へついていくと、
 *   あとから届いた自分の送信にほかの端末がついてきて、両方の端末が入れ替わって食い違うためである。
 * @failure: localStorage が使えない端末では、連動の切り替えを覚えない (読み込み直すと切れている)。
 */

import { useSyncExternalStore } from "react";

const ENABLED_KEY = "study.deviceSync.enabled";

let enabled = readEnabled();
const enabledListeners = new Set<() => void>();

function readEnabled(): boolean {
  try {
    return window.localStorage.getItem(ENABLED_KEY) === "1";
  } catch {
    return false;
  }
}

export function isDeviceSyncEnabled(): boolean {
  return enabled;
}

export function setDeviceSyncEnabled(next: boolean): void {
  enabled = next;
  try {
    if (next) window.localStorage.setItem(ENABLED_KEY, "1");
    else window.localStorage.removeItem(ENABLED_KEY);
  } catch {
    // 覚えられなくても、この画面を開いている間は切り替わる
  }
  for (const l of enabledListeners) l();
}

function subscribeEnabled(listener: () => void): () => void {
  enabledListeners.add(listener);
  // ほかのタブで切り替えたときも合わせる
  const onStorage = (e: StorageEvent) => {
    if (e.key !== ENABLED_KEY) return;
    enabled = readEnabled();
    listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    enabledListeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** 連動を入れているか (切り替えると表示し直す)。 */
export function useDeviceSyncEnabled(): boolean {
  return useSyncExternalStore(subscribeEnabled, isDeviceSyncEnabled, () => false);
}

function newDeviceId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
    return crypto.randomUUID();
  return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const deviceId = newDeviceId();

/** この画面を開いている間の端末の印である。 */
export function deviceSyncId(): string {
  return deviceId;
}

let seenAt = 0;

/** すでに見た閲覧位置の時刻 (epoch ms) である。 */
export function deviceSyncSeenAt(): number {
  return seenAt;
}

/** 自分が送った・ついていった閲覧位置の時刻を覚える (古い時刻では戻さない)。 */
export function noteDeviceSyncSeen(at: number): void {
  if (Number.isFinite(at) && at > seenAt) seenAt = at;
}

let sending = 0;
let idleWaiters: (() => void)[] = [];

/** 閲覧位置を送り始める (待ちの間も含む)。終わったら必ず endDeviceSyncSend を呼ぶ。 */
export function beginDeviceSyncSend(): void {
  sending += 1;
}

export function endDeviceSyncSend(): void {
  sending = Math.max(0, sending - 1);
  if (sending > 0) return;
  const waiters = idleWaiters;
  idleWaiters = [];
  for (const w of waiters) w();
}

/** 送っている途中の閲覧位置が無くなるまで待つ。 */
export function waitDeviceSyncSends(): Promise<void> {
  if (sending === 0) return Promise.resolve();
  return new Promise((resolve) => idleWaiters.push(resolve));
}

let followed: { sourceId: string; page: number } | null = null;

/** ほかの端末についていったページを覚える (このページは送り返さない)。 */
export function markDeviceSyncFollowed(sourceId: string, page: number): void {
  followed = { sourceId, page };
}

/**
 * ついていったページの印を、別のページへ付け替える (印が from を指しているときだけ)。
 *
 * @why: 見開きで読んでいる端末は、ほかの端末で開いた組の後のページへついていくと、組の先のページを
 *   「いまのページ」にする。印をそのままにすると先のページを送り返し、ほかの端末を1ページ前へ
 *   連れ戻すためである。
 */
export function retargetDeviceSyncFollowed(sourceId: string, from: number, to: number): void {
  if (followed && followed.sourceId === sourceId && followed.page === from) followed = { sourceId, page: to };
}

/** ついていったばかりのページかを見て、そうなら印を消して true を返す。 */
export function takeDeviceSyncFollowed(sourceId: string, page: number): boolean {
  if (!followed || followed.sourceId !== sourceId || followed.page !== page) return false;
  followed = null;
  return true;
}
